import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { ui } from '../translations';
import useSEO from '../hooks/useSEO';

const ContactPage = () => {
  const t = useT();
  const lang = t({ ro: 'ro', en: 'en' });
  useSEO({
    title: lang === 'ro'
      ? 'Contact | Bee-Advised – +40 744 324 272 | office@bee-advised.ro'
      : 'Contact | Bee-Advised – +40 744 324 272 | office@bee-advised.ro',
    description: lang === 'ro'
      ? 'Solicitați o cotație de preț de la Bee-Advised. Telefon: +40 744 324 272. Email: office@bee-advised.ro.'
      : 'Request a price quote from Bee-Advised. Phone: +40 744 324 272. Email: office@bee-advised.ro.',
    canonical: 'https://www.bee-advised.ro/contact',
    lang,
  });

  const infoCards = [
    { icon: Mail,  label: t({ ro: 'Email', en: 'Email' }),       value: 'office@bee-advised.ro',                              href: 'mailto:office@bee-advised.ro' },
    { icon: Phone, label: t({ ro: 'Telefon', en: 'Phone' }),     value: '+40 744 324 272',                                    href: 'tel:+40744324272' },
    { icon: Clock, label: t({ ro: 'Program', en: 'Hours' }),     value: t({ ro: 'Lun–Vin: 09:00 – 18:00', en: 'Mon–Fri: 09:00 – 18:00' }), href: null },
    { icon: MapPin, label: t({ ro: 'Acoperire', en: 'Coverage' }), value: t({ ro: 'Activăm în toată România', en: 'Operating throughout Romania' }), href: null },
  ];

  return (
    <div className="bg-[#0A192F] min-h-screen">
      {/* PAGE HERO */}
      <section data-testid="contact-hero" className="hex-pattern pt-32 pb-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-badge"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.contactPage.badge)}</div>
          <h1 className="text-5xl md:text-6xl font-black text-white font-heading tracking-tight leading-none mt-3">
            {t(ui.contactPage.title)}
          </h1>
          <p className="text-slate-400 mt-4 text-lg max-w-2xl leading-relaxed">{t(ui.contactPage.subtitle)}</p>
        </div>
      </section>

      {/* CONTACT INFO */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
            {infoCards.map((card, i) => {
              const Icon = card.icon;
              const inner = (
                <div className="flex items-start gap-5 p-7 border border-slate-700/60 bg-[#0D2137]/40 hover:border-[#00BCD4]/60 transition-all duration-300 h-full">
                  <div className="w-12 h-12 border border-slate-700 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-[#00BCD4]"/>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">{card.label}</p>
                    <p className="text-white font-medium text-base leading-snug">{card.value}</p>
                  </div>
                </div>
              );
              return card.href
                ? <a key={i} href={card.href} data-testid={`contact-info-${i}`} className="block">{inner}</a>
                : <div key={i} data-testid={`contact-info-${i}`}>{inner}</div>;
            })}
          </div>

          {/* CTA Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            <a href="tel:+40744324272" data-testid="contact-phone-cta"
              className="flex items-center justify-center gap-3 bg-[#00BCD4] text-[#0A192F] font-bold uppercase tracking-widest py-4 text-sm hover:bg-white transition-colors duration-200">
              <Phone size={16}/> +40 744 324 272
            </a>
            <a href="mailto:office@bee-advised.ro" data-testid="contact-email-cta"
              className="flex items-center justify-center gap-3 border border-[#00BCD4] text-[#00BCD4] font-bold uppercase tracking-widest py-4 text-sm hover:bg-[#00BCD4] hover:text-[#0A192F] transition-colors duration-200">
              <Mail size={16}/> office@bee-advised.ro
            </a>
          </div>

          {/* Google Maps */}
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-[#00BCD4]"/>
              {t({ ro: 'Locație', en: 'Location' })}
            </h2>
            <div 
              data-testid="google-map"
              className="relative w-full h-[400px] rounded-lg overflow-hidden border border-slate-700"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d45765.82028887385!2d23.7677869!3d44.3301785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4752d700ac4ca26b%3A0x4d0fa9c7ebc5d2cf!2sCraiova!5e0!3m2!1sen!2sro!4v1709500000000!5m2!1sen!2sro"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BEE-ADVISED Location"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 pointer-events-none border border-[#00BCD4]/20 rounded-lg"></div>
            </div>
            <p className="text-slate-500 text-sm mt-3 text-center">
              {t({ 
                ro: 'Sediul nostru este în Craiova, dar operăm în toată România', 
                en: 'Our headquarters is in Craiova, but we operate throughout Romania' 
              })}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
